/**
 * Super-admin Promo Banner manager. Lets ops edit every field, target pages,
 * pick a visual style, and track live impression/click counters. Reusable
 * for any future campaign — Emergent Builders Contest today, Black Friday
 * tomorrow.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Megaphone,
  Save,
  Loader2,
  Sparkles,
  Eye,
  MousePointerClick,
  ExternalLink,
  Plus,
  Trash2,
  ArrowRight,
  CalendarClock,
  FlaskConical,
  Crown,
  Play,
  StopCircle,
  Clock,
  ArrowBigUp,
  Gift,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";

interface VariantDraft {
  label: string;
  headline?: string;
  subline?: string;
  cta_label?: string;
  cta_url?: string;
}

interface VariantStats {
  impressions?: number;
  clicks?: number;
}

interface Banner {
  banner_id: string;
  enabled: boolean;
  headline: string;
  subline: string;
  cta_label: string;
  cta_url: string;
  upvote_url?: string | null;
  gift_label?: string | null;
  gift_url?: string | null;
  pages: string[];
  dismissible: boolean;
  style_variant: string;
  icon: string;
  priority: number;
  version: number;
  impressions: number;
  clicks: number;
  updated_at?: string;
  starts_at?: string | null;
  ends_at?: string | null;
  variants?: VariantDraft[];
  stats_by_variant?: Record<string, VariantStats>;
  show_header?: boolean;
  show_middle?: boolean;
  show_bottom?: boolean;
  show_popup?: boolean;
  popup_delay_seconds?: number;
}

const PAGE_OPTIONS: { key: string; label: string }[] = [
  { key: "home", label: "Home" },
  { key: "pricing", label: "Pricing" },
  { key: "login", label: "Login" },
  { key: "signup", label: "Signup" },
  { key: "marketplace", label: "Marketplace" },
  { key: "dashboard", label: "Dashboard (inside app)" },
  { key: "all", label: "Everywhere" },
];

const STYLE_OPTIONS: { key: string; label: string; className: string }[] = [
  { key: "violet", label: "Violet gradient (recommended)", className: "bg-[linear-gradient(90deg,#4c1d95,#9333ea,#c026d3)]" },
  { key: "saffron", label: "Saffron gradient", className: "bg-[linear-gradient(90deg,#7c2d12,#ea580c,#f97316)]" },
  { key: "dark", label: "Deep space", className: "bg-[linear-gradient(90deg,#020617,#0f172a,#1e293b)]" },
  { key: "minimal", label: "Minimal white", className: "bg-white border border-slate-200" },
];

const emptyDraft = (): Banner => ({
  banner_id: "",
  enabled: true,
  headline: "",
  subline: "",
  cta_label: "Learn more",
  cta_url: "",
  upvote_url: "",
  gift_label: "Submit for Gift",
  gift_url: "/gift",
  pages: ["home"],
  dismissible: false,
  style_variant: "violet",
  icon: "Sparkles",
  priority: 50,
  version: 1,
  impressions: 0,
  clicks: 0,
  starts_at: null,
  ends_at: null,
  variants: [],
  stats_by_variant: {},
  show_header: true,
  show_middle: false,
  show_bottom: false,
  show_popup: false,
  popup_delay_seconds: 5,
});

// Convert an ISO 8601 UTC string to a `<input type="datetime-local">` value
// (which uses the browser's local timezone, format YYYY-MM-DDTHH:mm).
function isoToLocal(iso?: string | null): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours(),
    )}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

function localToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

type ScheduleStatus = "live" | "scheduled" | "ended" | "always";

function scheduleStatus(startsAt?: string | null, endsAt?: string | null): ScheduleStatus {
  const now = Date.now();
  const start = startsAt ? new Date(startsAt).getTime() : null;
  const end = endsAt ? new Date(endsAt).getTime() : null;
  if (!start && !end) return "always";
  if (start && now < start) return "scheduled";
  if (end && now >= end) return "ended";
  return "live";
}

export const PromoBannersCard: React.FC = () => {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const readonly = (me?.role || "user") !== "admin";

  const list = useQuery<{ items: Banner[] }>({
    queryKey: ["promo-banners-admin"],
    queryFn: async () => (await api.get("/admin/promo-banners")).data,
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<Banner>(emptyDraft());

  // Auto-select first banner once loaded
  useEffect(() => {
    if (!selectedId && !creating && list.data?.items?.length) {
      const first = list.data.items[0];
      setSelectedId(first.banner_id);
      setDraft(first);
    }
  }, [list.data, selectedId, creating]);

  const activeBanner = useMemo(() => {
    if (creating) return draft;
    return list.data?.items.find((b) => b.banner_id === selectedId) || null;
  }, [list.data, selectedId, creating, draft]);

  // Sync draft to activeBanner when selection changes
  useEffect(() => {
    if (!creating && activeBanner) setDraft(activeBanner);
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...draft } as any;
      // Server won't accept these read-only fields on PUT
      delete body.version;
      delete body.impressions;
      delete body.clicks;
      delete body.updated_at;
      delete body.stats_by_variant;
      // Only send variants that actually override something
      if (Array.isArray(body.variants)) {
        body.variants = body.variants
          .filter((v: VariantDraft) => v.label && v.label.trim())
          .map((v: VariantDraft) => ({
            label: v.label.trim().slice(0, 16),
            headline: v.headline || null,
            subline: v.subline || null,
            cta_label: v.cta_label || null,
            cta_url: v.cta_url || null,
          }));
      }
      if (creating) {
        return (await api.post("/admin/promo-banners", body)).data as Banner;
      }
      const { banner_id, ...patch } = body;
      return (await api.put(`/admin/promo-banners/${banner_id}`, patch)).data as Banner;
    },
    onSuccess: (data) => {
      toast.success(creating ? "Banner created" : "Banner saved");
      setCreating(false);
      setSelectedId(data.banner_id);
      qc.invalidateQueries({ queryKey: ["promo-banners-admin"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const remove = useMutation({
    mutationFn: async (banner_id: string) =>
      (await api.delete(`/admin/promo-banners/${banner_id}`)).data,
    onSuccess: () => {
      toast.success("Banner removed");
      setSelectedId(null);
      qc.invalidateQueries({ queryKey: ["promo-banners-admin"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const togglePage = (p: string) => {
    setDraft((d) => {
      const has = d.pages.includes(p);
      return { ...d, pages: has ? d.pages.filter((x) => x !== p) : [...d.pages, p] };
    });
  };

  const startCreate = () => {
    const seed = emptyDraft();
    seed.banner_id = `promo-${Date.now().toString(36)}`;
    setDraft(seed);
    setCreating(true);
    setSelectedId(null);
  };

  const styleCls = STYLE_OPTIONS.find((s) => s.key === draft.style_variant)?.className || "";
  const ctr = draft.impressions > 0 ? ((draft.clicks / draft.impressions) * 100).toFixed(1) : "—";

  return (
    <div className="sg-card p-6" data-testid="promo-banners-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-fuchsia-50 dark:bg-fuchsia-950/40 flex items-center justify-center shrink-0">
          <Megaphone className="h-5 w-5 text-fuchsia-600 dark:text-fuchsia-400" />
        </div>
        <div className="flex-1">
          <div className="font-display text-lg font-bold text-[color:var(--sg-fg)]">Promo Banners</div>
          <div className="text-xs text-[color:var(--sg-muted)] mt-0.5">
            Slim top-of-page ribbons for launches, contests and campaigns. Fully configurable per
            page. Editing the copy resets user dismissals so everyone sees the new one.
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-5">
        {/* Left rail: list */}
        <div className="space-y-2">
          {list.isLoading ? (
            <div className="sg-skeleton h-16" />
          ) : (
            <>
              {(list.data?.items || []).map((b) => {
                const st = scheduleStatus(b.starts_at, b.ends_at);
                return (
                <button
                  key={b.banner_id}
                  type="button"
                  onClick={() => { setCreating(false); setSelectedId(b.banner_id); }}
                  data-testid={`promo-banner-item-${b.banner_id}`}
                  className={`w-full text-left rounded-lg border px-3 py-2.5 transition ${
                    selectedId === b.banner_id && !creating
                      ? "border-[color:var(--sg-primary)] bg-[color:var(--sg-primary)]/5"
                      : "border-[color:var(--sg-border)] hover:border-[color:var(--sg-muted)]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm truncate">{b.banner_id}</span>
                    <span className={`inline-block h-2 w-2 rounded-full ${b.enabled ? "bg-emerald-500" : "bg-slate-300"}`} />
                  </div>
                  <div className="text-[11px] text-[color:var(--sg-muted)] truncate mt-0.5">{b.headline || "—"}</div>
                  <div className="text-[10px] text-[color:var(--sg-muted)] mt-1 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {b.impressions}</span>
                    <span className="inline-flex items-center gap-1"><MousePointerClick className="h-3 w-3" /> {b.clicks}</span>
                    {st === "live" && b.enabled && <span className="inline-flex items-center gap-1 text-emerald-600 font-medium"><Play className="h-3 w-3" /> Live</span>}
                    {st === "scheduled" && <span className="inline-flex items-center gap-1 text-blue-600 font-medium"><CalendarClock className="h-3 w-3" /> Scheduled</span>}
                    {st === "ended" && <span className="inline-flex items-center gap-1 text-slate-500 font-medium"><StopCircle className="h-3 w-3" /> Ended</span>}
                    {(b.variants && b.variants.length > 0) && <span className="inline-flex items-center gap-1 text-fuchsia-700 font-medium"><FlaskConical className="h-3 w-3" /> A/B</span>}
                  </div>
                </button>
                );
              })}
              {!readonly && (
                <button
                  type="button"
                  onClick={startCreate}
                  data-testid="promo-banner-new"
                  className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-[color:var(--sg-border)] px-3 py-2.5 text-xs font-semibold text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)] hover:border-[color:var(--sg-fg)] transition"
                >
                  <Plus className="h-3.5 w-3.5" /> New banner
                </button>
              )}
            </>
          )}
        </div>

        {/* Right pane: editor */}
        <div className="space-y-4">
          {!activeBanner ? (
            <div className="text-sm text-[color:var(--sg-muted)] rounded-lg border border-dashed border-[color:var(--sg-border)] p-8 text-center">
              Pick a banner from the left, or create a new one.
            </div>
          ) : (
            <>
              {/* Live preview */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">
                  Live preview
                </Label>
                <div className={`mt-1.5 rounded-lg overflow-hidden ${styleCls}`} data-testid="promo-banner-preview">
                  <div className="text-white flex items-center gap-3 px-4 py-2.5">
                    <div className="h-7 w-7 shrink-0 rounded-full bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/25">
                      <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </div>
                    <div className="min-w-0 flex-1 flex flex-wrap items-center gap-x-3">
                      <span className="text-sm font-semibold tracking-tight truncate">{draft.headline || "Your headline here"}</span>
                      {draft.subline ? <span className="text-[12.5px] opacity-85 truncate">{draft.subline}</span> : null}
                    </div>
                    {draft.upvote_url ? (
                      <span className="hidden sm:inline-flex shrink-0 items-center gap-1 rounded-full bg-white/10 text-white text-[11.5px] font-semibold px-2.5 py-1 ring-1 ring-white/25">
                        <ArrowBigUp className="h-3.5 w-3.5" strokeWidth={2.5} />
                        <span className="uppercase text-[10px] tracking-wider">Upvote</span>
                      </span>
                    ) : null}
                    <PreviewCountdown endsAt={draft.ends_at} />
                    <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-white/95 text-slate-900 text-[13px] font-semibold px-3.5 py-1.5">
                      {draft.cta_label || "CTA"} <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </span>
                  </div>
                </div>
              </div>

              {/* Stats row */}
              {!creating && (
                <div className="grid grid-cols-3 gap-2">
                  <Stat label="Impressions" value={draft.impressions.toLocaleString()} />
                  <Stat label="Clicks" value={draft.clicks.toLocaleString()} />
                  <Stat label="CTR" value={ctr === "—" ? "—" : `${ctr}%`} />
                </div>
              )}

              {/* Enable + IDs */}
              <div className="flex items-center justify-between gap-3 rounded-lg border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 px-3 py-2.5">
                <div className="min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-fg)]">
                    {creating ? "New banner" : draft.banner_id}
                  </div>
                  <div className="text-[11px] text-[color:var(--sg-muted)] mt-0.5">
                    {draft.enabled ? "Enabled — visible on selected pages." : "Disabled — hidden everywhere."}
                  </div>
                </div>
                <Switch
                  checked={draft.enabled}
                  disabled={readonly}
                  data-testid="promo-banner-enabled"
                  onCheckedChange={(v) => setDraft((d) => ({ ...d, enabled: !!v }))}
                />
              </div>

              {creating && (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Banner ID (lowercase-slug)</Label>
                  <Input
                    data-testid="promo-banner-id"
                    className="mt-1 font-mono text-xs"
                    value={draft.banner_id}
                    onChange={(e) => setDraft((d) => ({ ...d, banner_id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") }))}
                  />
                </div>
              )}

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Headline</Label>
                <Input
                  data-testid="promo-banner-headline-input"
                  className="mt-1"
                  value={draft.headline}
                  disabled={readonly}
                  onChange={(e) => setDraft((d) => ({ ...d, headline: e.target.value }))}
                />
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Subline (optional)</Label>
                <Input
                  data-testid="promo-banner-subline-input"
                  className="mt-1"
                  value={draft.subline}
                  disabled={readonly}
                  onChange={(e) => setDraft((d) => ({ ...d, subline: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">CTA label</Label>
                  <Input
                    data-testid="promo-banner-cta-label"
                    className="mt-1"
                    value={draft.cta_label}
                    disabled={readonly}
                    onChange={(e) => setDraft((d) => ({ ...d, cta_label: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Priority (higher wins)</Label>
                  <Input
                    type="number"
                    data-testid="promo-banner-priority"
                    className="mt-1"
                    value={draft.priority}
                    disabled={readonly}
                    onChange={(e) => setDraft((d) => ({ ...d, priority: parseInt(e.target.value || "0", 10) || 0 }))}
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">CTA URL (full or /path)</Label>
                <Input
                  data-testid="promo-banner-cta-url"
                  className="mt-1 font-mono text-xs"
                  placeholder="https://…"
                  value={draft.cta_url}
                  disabled={readonly}
                  onChange={(e) => setDraft((d) => ({ ...d, cta_url: e.target.value }))}
                />
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)] flex items-center gap-1.5">
                  <ArrowBigUp className="h-3.5 w-3.5" /> Upvote URL (optional)
                </Label>
                <Input
                  data-testid="promo-banner-upvote-url"
                  className="mt-1 font-mono text-xs"
                  placeholder="https://app.emergent.sh/showcase/…"
                  value={draft.upvote_url || ""}
                  disabled={readonly}
                  onChange={(e) => setDraft((d) => ({ ...d, upvote_url: e.target.value }))}
                />
                <div className="mt-1 text-[11px] text-[color:var(--sg-muted)]">
                  Shows a small "Upvote" pill to the left of the countdown. Opens in a new tab. Leave blank to hide.
                </div>
              </div>

              {/* Gift CTA — routes visitors to the Gift submission page.
                  Distinct from cta_url so admins can run BOTH (e.g. an
                  Emergent contest link AND our own gift funnel). */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-1">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)] flex items-center gap-1.5">
                    <Gift className="h-3.5 w-3.5" /> Gift label
                  </Label>
                  <Input
                    data-testid="promo-banner-gift-label"
                    className="mt-1"
                    placeholder="Submit for Gift"
                    value={draft.gift_label || ""}
                    disabled={readonly}
                    onChange={(e) => setDraft((d) => ({ ...d, gift_label: e.target.value }))}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Gift URL</Label>
                  <Input
                    data-testid="promo-banner-gift-url"
                    className="mt-1 font-mono text-xs"
                    placeholder="/gift"
                    value={draft.gift_url || ""}
                    disabled={readonly}
                    onChange={(e) => setDraft((d) => ({ ...d, gift_url: e.target.value }))}
                  />
                  <div className="mt-1 text-[11px] text-[color:var(--sg-muted)]">
                    Adds a "Submit for Gift" pill immediately after Upvote. Use <code>/gift</code> to stay in-app, or a full <code>https://…</code> URL. Leave blank to hide.
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Show on</Label>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {PAGE_OPTIONS.map((p) => {
                    const on = draft.pages.includes(p.key);
                    return (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => togglePage(p.key)}
                        data-testid={`promo-banner-page-${p.key}`}
                        disabled={readonly}
                        className={`text-xs font-semibold px-2.5 py-1.5 rounded-full border transition ${
                          on
                            ? "bg-[color:var(--sg-fg)] text-white border-[color:var(--sg-fg)]"
                            : "bg-white text-[color:var(--sg-muted)] border-[color:var(--sg-border)] hover:border-[color:var(--sg-fg)]"
                        }`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Style</Label>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  {STYLE_OPTIONS.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => setDraft((d) => ({ ...d, style_variant: s.key }))}
                      data-testid={`promo-banner-style-${s.key}`}
                      disabled={readonly}
                      className={`text-left rounded-md border px-2.5 py-2 transition ${
                        draft.style_variant === s.key
                          ? "border-[color:var(--sg-primary)] ring-2 ring-[color:var(--sg-primary)]/20"
                          : "border-[color:var(--sg-border)] hover:border-[color:var(--sg-muted)]"
                      }`}
                    >
                      <div className={`h-4 w-full rounded ${s.className}`} />
                      <div className="text-[11px] font-medium mt-1.5">{s.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Schedule */}
              <ScheduleSection draft={draft} setDraft={setDraft} readonly={readonly} />

              {/* A/B experiments */}
              <ABSection draft={draft} setDraft={setDraft} readonly={readonly} />

              <div className="flex items-center justify-between gap-3 rounded-lg border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 px-3 py-2.5">
                <div className="min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-fg)]">Allow users to close contest banner</div>
                  <div className="text-[11px] text-[color:var(--sg-muted)] mt-0.5">
                    {draft.dismissible
                      ? "Enabled — the (×) button appears and users can dismiss the banner (remembered per version)."
                      : "Disabled (recommended) — no (×) button. Banner stays visible until you disable it here."}
                  </div>
                </div>
                <Switch
                  checked={draft.dismissible}
                  disabled={readonly}
                  data-testid="promo-banner-dismissible"
                  onCheckedChange={(v) => setDraft((d) => ({ ...d, dismissible: !!v }))}
                />
              </div>

              {/* PLACEMENTS: same banner content mounted in up to 4 places
                  at once (header, middle of page, bottom-sticky, popup). */}
              <div className="rounded-xl border border-[color:var(--sg-border)] p-3 space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Placements</div>
                {([
                  ["show_header", "Show as header banner"],
                  ["show_middle", "Show as middle-of-page callout"],
                  ["show_bottom", "Show as bottom sticky bar"],
                  ["show_popup", "Show as centered popup"],
                ] as [keyof Banner, string][]).map(([key, label]) => (
                  <label key={key as string} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-[color:var(--sg-saffron,#f97316)]"
                      checked={!!(draft as any)[key]}
                      disabled={readonly}
                      data-testid={`promo-placement-${(key as string).replace("show_", "")}`}
                      onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.checked } as Banner))}
                    />
                    <span>{label}</span>
                  </label>
                ))}
                <div className="flex items-center gap-2 pt-2 border-t border-[color:var(--sg-border)]">
                  <Label className="text-xs whitespace-nowrap">Popup delay (seconds)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={120}
                    className="h-8 w-20 text-xs"
                    data-testid="promo-popup-delay"
                    disabled={readonly || !draft.show_popup}
                    value={draft.popup_delay_seconds ?? 5}
                    onChange={(e) => setDraft((d) => ({ ...d, popup_delay_seconds: Math.max(0, parseInt(e.target.value || "0", 10)) }))}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 pt-2">
                <div className="text-[11px] text-[color:var(--sg-muted)]">
                  {draft.updated_at ? <>Last saved {new Date(draft.updated_at).toLocaleString()} · version {draft.version}</> : "Unsaved draft"}
                </div>
                <div className="flex items-center gap-2">
                  {!creating && !readonly && draft.banner_id !== "emergent-launch" && (
                    <Button
                      data-testid="promo-banner-delete"
                      variant="outline"
                      size="sm"
                      className="border-red-200 text-red-700 hover:bg-red-50 h-8"
                      onClick={() => {
                        if (window.confirm(`Delete banner "${draft.banner_id}"?`)) remove.mutate(draft.banner_id);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                    </Button>
                  )}
                  {draft.cta_url && (
                    <a
                      href={draft.cta_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[color:var(--sg-primary)] hover:underline inline-flex items-center gap-1"
                    >
                      Test link <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {!readonly && (
                    <Button
                      data-testid="promo-banner-save"
                      onClick={() => save.mutate()}
                      disabled={save.isPending || !draft.headline.trim() || !draft.cta_url.trim() || draft.pages.length === 0}
                      className="bg-[color:var(--sg-fg)] text-white hover:bg-black h-8"
                    >
                      {save.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                      {creating ? "Create" : "Save changes"}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-md border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)] px-3 py-2">
    <div className="text-[10px] uppercase tracking-wide text-[color:var(--sg-muted)]">{label}</div>
    <div className="text-sm font-semibold mt-0.5 text-[color:var(--sg-fg)]">{value}</div>
  </div>
);

// ---------------- Schedule ----------------
const ScheduleSection: React.FC<{
  draft: Banner;
  setDraft: React.Dispatch<React.SetStateAction<Banner>>;
  readonly: boolean;
}> = ({ draft, setDraft, readonly }) => {
  const st = scheduleStatus(draft.starts_at, draft.ends_at);
  const stChipCls =
    st === "live"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : st === "scheduled"
      ? "bg-blue-50 text-blue-700 border-blue-200"
      : st === "ended"
      ? "bg-slate-100 text-slate-600 border-slate-200"
      : "bg-white text-slate-600 border-slate-200";
  const stLabel = st === "always" ? "No schedule — runs whenever enabled" : st;

  return (
    <div
      data-testid="promo-banner-schedule"
      className="rounded-lg border border-[color:var(--sg-border)] p-4 bg-[color:var(--sg-muted-bg)]/40"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="h-7 w-7 rounded-lg bg-blue-100 dark:bg-blue-950/40 flex items-center justify-center">
          <CalendarClock className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-[color:var(--sg-fg)]">Schedule</div>
          <div className="text-[11px] text-[color:var(--sg-muted)]">
            Optional. Banner is hidden outside this window even when Enabled is on.
          </div>
        </div>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${stChipCls}`}>
          {stLabel}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">
            Starts at (your local timezone)
          </Label>
          <div className="mt-1 flex items-center gap-1.5">
            <Input
              type="datetime-local"
              data-testid="promo-banner-starts-at"
              className="flex-1"
              value={isoToLocal(draft.starts_at)}
              disabled={readonly}
              onChange={(e) => setDraft((d) => ({ ...d, starts_at: localToIso(e.target.value) }))}
            />
            {draft.starts_at ? (
              <button
                type="button"
                onClick={() => setDraft((d) => ({ ...d, starts_at: "" as any }))}
                className="text-[11px] text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)] px-1.5"
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">
            Ends at
          </Label>
          <div className="mt-1 flex items-center gap-1.5">
            <Input
              type="datetime-local"
              data-testid="promo-banner-ends-at"
              className="flex-1"
              value={isoToLocal(draft.ends_at)}
              disabled={readonly}
              onChange={(e) => setDraft((d) => ({ ...d, ends_at: localToIso(e.target.value) }))}
            />
            {draft.ends_at ? (
              <button
                type="button"
                onClick={() => setDraft((d) => ({ ...d, ends_at: "" as any }))}
                className="text-[11px] text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)] px-1.5"
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>
      </div>
      {draft.starts_at && draft.ends_at && new Date(draft.starts_at) >= new Date(draft.ends_at) ? (
        <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          Heads up — end time is before or equal to start time. Nothing will render.
        </div>
      ) : null}
      <div
        className="mt-2 text-[10.5px] text-[color:var(--sg-muted)] flex items-center gap-1.5"
        data-testid="promo-banner-schedule-tz-hint"
      >
        <Clock className="h-3 w-3 opacity-70" />
        <span>
          Shown in your local time
          {(() => {
            try {
              const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
              return tz ? <> (<span className="font-mono">{tz}</span>)</> : null;
            } catch {
              return null;
            }
          })()}
          {" · "}saved as UTC.
        </span>
      </div>
    </div>
  );
};

// ---------------- A/B Experiments ----------------
const ABSection: React.FC<{
  draft: Banner;
  setDraft: React.Dispatch<React.SetStateAction<Banner>>;
  readonly: boolean;
}> = ({ draft, setDraft, readonly }) => {
  const qc = useQueryClient();
  const variants = draft.variants || [];
  const stats = draft.stats_by_variant || {};

  const promote = useMutation({
    mutationFn: async () =>
      (await api.post(`/admin/promo-banners/${draft.banner_id}/promote-winner`)).data as Banner,
    onSuccess: (data) => {
      toast.success(`Promoted variant ${data.headline ? "✨" : ""} — experiment closed.`);
      qc.invalidateQueries({ queryKey: ["promo-banners-admin"] });
      // Sync local draft to the promoted banner so the panel reflects reality
      setDraft(data);
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Promote failed"),
  });

  const winner = React.useMemo(() => {
    // Winner = variant with highest CTR, needs at least 20 impressions per variant
    let best: { label: string; ctr: number } | null = null;
    for (const v of variants) {
      const s = stats[v.label] || {};
      const imp = s.impressions || 0;
      const clk = s.clicks || 0;
      if (imp < 20) continue;
      const ctr = imp > 0 ? clk / imp : 0;
      if (!best || ctr > best.ctr) best = { label: v.label, ctr };
    }
    return best;
  }, [variants, stats]);

  // Promote is only offered when every variant has ≥ 20 imp AND the winner has clicks
  const canPromote = React.useMemo(() => {
    if (variants.length < 2 || !winner) return false;
    const allReady = variants.every((v) => (stats[v.label]?.impressions || 0) >= 20);
    const winnerClicks = stats[winner.label]?.clicks || 0;
    return allReady && winnerClicks > 0;
  }, [variants, stats, winner]);

  const addVariant = () => {
    const usedLabels = new Set(variants.map((v) => v.label));
    const alphabet = ["A", "B", "C", "D", "E"];
    const label = alphabet.find((a) => !usedLabels.has(a)) || `V${variants.length + 1}`;
    setDraft((d) => ({
      ...d,
      variants: [...(d.variants || []), { label, headline: d.headline || "" }],
    }));
  };

  const updateVariant = (idx: number, patch: Partial<VariantDraft>) => {
    setDraft((d) => ({
      ...d,
      variants: (d.variants || []).map((v, i) => (i === idx ? { ...v, ...patch } : v)),
    }));
  };

  const removeVariant = (idx: number) => {
    setDraft((d) => ({
      ...d,
      variants: (d.variants || []).filter((_, i) => i !== idx),
    }));
  };

  return (
    <div
      data-testid="promo-banner-ab"
      className="rounded-lg border border-[color:var(--sg-border)] p-4 bg-[color:var(--sg-muted-bg)]/40"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="h-7 w-7 rounded-lg bg-fuchsia-100 dark:bg-fuchsia-950/40 flex items-center justify-center">
          <FlaskConical className="h-3.5 w-3.5 text-fuchsia-600 dark:text-fuchsia-400" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-[color:var(--sg-fg)]">A/B experiments</div>
          <div className="text-[11px] text-[color:var(--sg-muted)]">
            Add variants to A/B test headline and CTA. One is picked at random per pageview. We
            crown a winner once each variant has ≥ 20 impressions.
          </div>
        </div>
        {variants.length === 0 && !readonly ? (
          <button
            type="button"
            onClick={addVariant}
            data-testid="promo-banner-ab-start"
            className="inline-flex items-center gap-1 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-[11px] font-semibold px-3 py-1.5"
          >
            <Plus className="h-3.5 w-3.5" /> Start experiment
          </button>
        ) : null}
      </div>

      {variants.length === 0 ? (
        <div className="text-[12px] text-[color:var(--sg-muted)] rounded border border-dashed border-[color:var(--sg-border)] p-3 text-center">
          No experiment running. When you're ready, click "Start experiment" — variant A will use
          your current copy, then add B/C/… with different headlines or CTAs.
        </div>
      ) : (
        <div className="space-y-2.5">
          {variants.map((v, idx) => {
            const s = stats[v.label] || {};
            const imp = s.impressions || 0;
            const clk = s.clicks || 0;
            const ctr = imp > 0 ? (clk / imp) * 100 : 0;
            const isWinner = winner?.label === v.label;
            return (
              <div
                key={idx}
                data-testid={`promo-banner-variant-${v.label}`}
                className={`rounded-md border p-2.5 ${
                  isWinner
                    ? "border-amber-300 bg-amber-50/60"
                    : "border-[color:var(--sg-border)] bg-white"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Input
                    className="w-14 h-7 text-center font-mono font-semibold uppercase text-xs"
                    value={v.label}
                    maxLength={4}
                    disabled={readonly}
                    onChange={(e) => updateVariant(idx, { label: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })}
                    data-testid={`promo-banner-variant-${v.label}-label`}
                  />
                  {isWinner ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                      <Crown className="h-3 w-3" /> Winner
                    </span>
                  ) : null}
                  <div className="ml-auto flex items-center gap-3 text-[11px] text-[color:var(--sg-muted)]">
                    <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />{imp}</span>
                    <span className="inline-flex items-center gap-1"><MousePointerClick className="h-3 w-3" />{clk}</span>
                    <span className={`font-semibold ${ctr > 0 ? "text-[color:var(--sg-fg)]" : ""}`}>{imp >= 20 ? `${ctr.toFixed(1)}% CTR` : imp > 0 ? `${imp}/20 imp` : "—"}</span>
                  </div>
                  {!readonly && (
                    <button
                      type="button"
                      onClick={() => removeVariant(idx)}
                      data-testid={`promo-banner-variant-${v.label}-remove`}
                      className="text-slate-400 hover:text-red-600 p-1"
                      title="Remove variant"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input
                    placeholder="Headline (overrides base)"
                    className="h-8 text-xs"
                    value={v.headline || ""}
                    disabled={readonly}
                    onChange={(e) => updateVariant(idx, { headline: e.target.value })}
                  />
                  <Input
                    placeholder="CTA label"
                    className="h-8 text-xs"
                    value={v.cta_label || ""}
                    disabled={readonly}
                    onChange={(e) => updateVariant(idx, { cta_label: e.target.value })}
                  />
                  <Input
                    placeholder="Subline (optional)"
                    className="h-8 text-xs"
                    value={v.subline || ""}
                    disabled={readonly}
                    onChange={(e) => updateVariant(idx, { subline: e.target.value })}
                  />
                  <Input
                    placeholder="CTA URL (leave blank to inherit)"
                    className="h-8 text-xs font-mono"
                    value={v.cta_url || ""}
                    disabled={readonly}
                    onChange={(e) => updateVariant(idx, { cta_url: e.target.value })}
                  />
                </div>
              </div>
            );
          })}
          {!readonly && (
            <button
              type="button"
              onClick={addVariant}
              data-testid="promo-banner-ab-add"
              className="w-full flex items-center justify-center gap-1.5 rounded-md border border-dashed border-[color:var(--sg-border)] px-3 py-2 text-xs font-semibold text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)] hover:border-[color:var(--sg-fg)] transition"
            >
              <Plus className="h-3.5 w-3.5" /> Add variant
            </button>
          )}
          {winner ? (
            <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-2.5 py-2">
              <Crown className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1 text-[11px] text-amber-800">
                Variant <b>{winner.label}</b> is leading with <b>{(winner.ctr * 100).toFixed(1)}% CTR</b>.
                {canPromote
                  ? " Ready to lock it in — clicking promotes the copy to the base and ends the experiment."
                  : " Waiting for more data before you can auto-promote."}
              </div>
              {canPromote && !readonly ? (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(
                      `Promote variant ${winner.label} to the base copy and end the experiment? This will merge its headline / subline / CTA into the main banner and clear all variants + variant stats.`
                    )) {
                      promote.mutate();
                    }
                  }}
                  disabled={promote.isPending}
                  data-testid="promo-banner-promote-winner"
                  className="shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white text-[11px] font-semibold px-2.5 py-1 transition"
                >
                  {promote.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Crown className="h-3 w-3" />}
                  Promote to base
                </button>
              ) : null}
            </div>
          ) : variants.some((v) => (stats[v.label]?.impressions || 0) > 0) ? (
            <div className="text-[11px] text-[color:var(--sg-muted)]">
              Collecting data — each variant needs ≥ 20 impressions before a winner is called.
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

// ---------------- Preview countdown ----------------
// Mirrors the live PromoBanner countdown so the admin preview feels real.
const PreviewCountdown: React.FC<{ endsAt?: string | null }> = ({ endsAt }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!endsAt) return;
    const target = new Date(endsAt).getTime();
    if (isNaN(target)) return;
    const remaining = target - Date.now();
    const rate = remaining <= 3_600_000 ? 1000 : 60_000;
    const t = setInterval(() => setNow(Date.now()), rate);
    return () => clearInterval(t);
  }, [endsAt]);

  if (!endsAt) return null;
  const target = new Date(endsAt).getTime();
  if (isNaN(target)) return null;
  const diff = target - now;
  if (diff <= 0) return null;

  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  const secs = Math.floor((diff % 60_000) / 1000);

  let label: string;
  if (days >= 2) label = `${days}d ${hours}h left`;
  else if (days >= 1) label = `${days}d ${hours}h ${mins}m`;
  else if (hours >= 1) label = `${hours}h ${mins}m ${String(secs).padStart(2, "0")}s`;
  else label = `${mins}:${String(secs).padStart(2, "0")}`;

  const urgent = diff <= 3_600_000;
  return (
    <span
      className={`hidden sm:inline-flex shrink-0 items-center gap-1.5 rounded-full backdrop-blur px-2.5 py-1 text-[11.5px] font-semibold tabular-nums ring-1 ${
        urgent ? "bg-red-500/20 text-white ring-red-300/40 animate-pulse" : "bg-white/10 text-white ring-white/25"
      }`}
    >
      <Clock className="h-3 w-3" strokeWidth={2.5} />
      <span className="opacity-80 uppercase text-[10px] tracking-wider">Ends in</span>
      <span>{label}</span>
    </span>
  );
};

export default PromoBannersCard;
